import React, { useEffect, useRef } from 'react';

interface DivWithInertProps extends React.HTMLAttributes<HTMLDivElement> {
	inert?: boolean;
}

/**
 * A custom `div` component that sets the `inert` attribute to make all underlying children
 * non-interactive when the `inert` prop is set to true.
 *
 * @param {boolean} inert - Determines whether the element and its children should be non-interactive.
 * @param {React.ReactNode} children - The child elements to be rendered within the `div`.
 * @param {React.HTMLAttributes<HTMLDivElement>} props - Additional HTML attributes to be applied to the `div`.
 *
 * @returns {JSX.Element} A `div` element with the `inert` attribute applied based on the `inert` prop.
 */
const DivWithInert: React.FC<DivWithInertProps> = ({
	inert,
	children,
	...props
}: DivWithInertProps): JSX.Element => {
	const divRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (divRef.current) {
			if (inert) {
				divRef.current.setAttribute('inert', '');
				// Additional logic to make elements non-interactive if the browser does not support `inert`
				divRef.current
					.querySelectorAll(
						'a, button, input, textarea, select, details, [tabindex]'
					)
					.forEach((el) => {
						el.setAttribute('tabindex', '-1');
						el.setAttribute('aria-hidden', 'true');
						el.setAttribute('disabled', 'true');
					});
			} else {
				divRef.current.removeAttribute('inert');
				// Reverting the non-interactive state
				divRef.current
					.querySelectorAll(
						'a, button, input, textarea, select, details, [tabindex]'
					)
					.forEach((el) => {
						el.removeAttribute('tabindex');
						el.removeAttribute('aria-hidden');
						el.removeAttribute('disabled');
					});
			}
		}
	}, [inert]);

	return (
		<div
			className='wlcb-w-full'
			ref={divRef}
			{...props}
		>
			{children}
		</div>
	);
};

export default DivWithInert;
